// Ultimate Card Game (UCG) - First-Time Player Onboarding ("Commander Briefing")
//
// A short, non-blocking 3-step guide shown once per browser the first time
// a brand-new player reaches the default single-player match (see
// Game.init()). Presented as the player's own Commander briefing them
// through the game's existing speech-bubble visual language (the chamfered
// panel/glow recipe from css/main.css's .taunt-speech-bubble, see
// js/ui/tauntManager.js) rather than generic tooltip chrome, so it reads as
// part of the game instead of a bolted-on tutorial widget - see
// docs/SCALING_AUDIT_KRITERIENKATALOG.md K-3.4.
//
// Deliberately non-blocking: the match keeps running underneath (no pause,
// no backdrop dimmer, nothing else on screen loses pointer-events) - a step
// advances either the moment the player actually does the thing it
// describes (auto-advance, polled against real game state) or via the
// Next button, whichever comes first.

const STORAGE_KEY = 'ucg_onboarding_done';
const POLL_MS = 400;
const START_DELAY_MS = 700; // let the match-start stinger/HUD settle first

export class OnboardingGuide {
  constructor(game) {
    this.game = game;
    this.stepIndex = -1;
    this.active = false;
    this.layerEl = null;
    this.bubbleEl = null;
    this._highlightEl = null;
    this._pollTimer = null;
    this._baseline = null;
    this._boundReposition = () => this._positionBubble();
  }

  static hasCompleted() {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  }

  // Called once, shortly after Game.init() renders its first frame. No-op
  // if this browser has already seen it.
  maybeStart() {
    if (OnboardingGuide.hasCompleted()) return;
    setTimeout(() => this._begin(), START_DELAY_MS);
  }

  // Explicit re-trigger from the setup drawer's "Tutorial" button (see
  // js/main.js) - ignores the completed flag, always usable again.
  replay() {
    this._begin();
  }

  _begin() {
    if (this.active || typeof document === 'undefined') return;
    // A campaign mission (docs/singleplayer implementation soft-foraging-
    // flamingo.md) owns the screen with its own narrative reveal - this
    // generic PvP tutorial has no business starting mid-cutscene (the
    // START_DELAY_MS timer from maybeStart() can still be pending when the
    // player reaches the campaign hotspot within that window).
    if (this.game?.isCampaignMission) return;
    this.active = true;
    this._buildDom();
    window.addEventListener('resize', this._boundReposition);
    this._showStep(0);
  }

  // Cancels an already-active guide WITHOUT marking it "completed" (unlike
  // _finish() below) - used when a campaign mission starts while the guide
  // is still mid-sequence, so its hint bubble can't sit on top of the
  // cinematic HUD's own menu button. The player still sees the real guide
  // the next time they start a normal match.
  cancelForCampaign() {
    if (!this.active) return;
    clearInterval(this._pollTimer);
    this._pollTimer = null;
    this._clearHighlight();
    window.removeEventListener('resize', this._boundReposition);
    if (this.layerEl?.parentNode) this.layerEl.remove();
    this.layerEl = null;
    this.bubbleEl = null;
    this.active = false;
  }

  _buildDom() {
    if (this.layerEl) return;
    const layer = document.createElement('div');
    layer.id = 'onboarding-layer';
    layer.className = 'onboarding-layer';
    (document.getElementById('game-wrapper') || document.body).appendChild(layer);
    this.layerEl = layer;

    const bubble = document.createElement('div');
    bubble.id = 'onboarding-bubble';
    bubble.className = 'onboarding-bubble team-western';
    layer.appendChild(bubble);
    this.bubbleEl = bubble;
  }

  // Content is resolved fresh each time a step shows (not cached at
  // construction) purely so step 3's melting explanation reads correctly
  // whichever base race the player actually picked - the default match
  // (Game.init()) is always Human, but replay() can fire mid-Sul-game too.
  get steps() {
    const isSul = (this.game?.battlefield?.bases?.western?.race || 'Human') === 'Sul';
    const isMobile = !!this.game?.performanceManager?.isMobile;
    const hotkeyRow = (key, desc) => `<div class="onboarding-hotkey-row"><span class="onboarding-hotkey-key">${key}</span>${desc}</div>`;
    return [
      {
        title: 'DEPLOY A UNIT',
        text: isMobile
          ? 'Cards in your hand cost Plasma to deploy. Tap one to send it into the lane.'
          : 'Cards in your hand cost Plasma to deploy. Click one to send it into the lane, or press 1-5 to play the matching slot and D to draw into the first empty one.',
        highlight: () => document.getElementById('cards-hand-container'),
        autoAdvance: () => this._cardsPlayed('western') > this._baseline.cardsPlayed
      },
      {
        title: 'HOLD THE LINE',
        text: 'The line down the middle marks the front. Push units past it to claim territory and starve the enemy economy.',
        highlight: () => document.getElementById('battlefield-canvas'),
        autoAdvance: null
      },
      {
        title: 'MELTING',
        text: isSul
          ? 'On Sul, three IDENTICAL units fuse into a random unit of the next caste up, automatically. Human decks melt three IDENTICAL cards into a specialized Hyper unit.'
          : 'On Humans, three IDENTICAL cards in hand fuse into a specialized Hyper unit, automatically. Sul decks melt three IDENTICAL cards into a random unit of the next caste up.',
        highlight: () => document.getElementById('cards-hand-container'),
        autoAdvance: () => (this.game?.cardSystem?.meltSeq || 0) > this._baseline.meltSeq
      },
      // Desktop-only: every hotkey here is a keyboard shortcut, so there is
      // nothing for a touch player to act on - see _showStep() below, which
      // is expected to just never index this far on mobile.
      isMobile ? null : {
        title: 'HOTKEYS',
        text: `<div class="onboarding-hotkey-list">${[
          hotkeyRow('1-5', 'play or draw the matching hand slot'),
          hotkeyRow('D', 'draws into the first empty slot'),
          hotkeyRow('N', 'fires Superweapon at the midline'),
          hotkeyRow('F', 'Fullscreen'),
          hotkeyRow('X', 'toggles 4x speed'),
          hotkeyRow('C', 'toggles Auto-Draw'),
          hotkeyRow('P', 'toggles Performance HUD')
        ].join('')}</div>`,
        highlight: null,
        autoAdvance: null
      }
    ].filter(Boolean);
  }

  _cardsPlayed(team) {
    return this.game?.battlefield?.stats?.getSummary?.(team)?.cardsPlayed || 0;
  }

  _captureBaseline() {
    this._baseline = {
      cardsPlayed: this._cardsPlayed('western'),
      meltSeq: this.game?.cardSystem?.meltSeq || 0
    };
  }

  _showStep(index) {
    const steps = this.steps;
    if (index >= steps.length) {
      this._finish();
      return;
    }
    this.stepIndex = index;
    this._captureBaseline();
    const step = steps[index];

    // Batch 4 (docs/SOUND_BATCH_PLAN.md section 4.4) - erstes akustisches
    // Feedback im gesamten Tutorial, inkl. Schritt 0.
    this.game?.audio?.playOnboardingStep?.();

    this._clearHighlight();
    const target = step.highlight?.();
    if (target) {
      target.classList.add('onboarding-spotlight');
      this._highlightEl = target;
    }

    this.bubbleEl.innerHTML = `
      <div class="onboarding-bubble-inner">
        <span class="bubble-chevron" aria-hidden="true"></span>
        <div class="onboarding-bubble-head">
          <span class="onboarding-bubble-label">COMMANDER</span>
          <span class="onboarding-bubble-step">${index + 1}/${steps.length}</span>
        </div>
        <div class="onboarding-bubble-title">${step.title}</div>
        <div class="onboarding-bubble-text">${step.text}</div>
        <div class="onboarding-bubble-actions">
          <button type="button" class="onboarding-btn onboarding-btn-skip">SKIP</button>
          <button type="button" class="onboarding-btn onboarding-btn-next">${index === steps.length - 1 ? 'GOT IT' : 'NEXT'}</button>
        </div>
      </div>
    `;

    this.bubbleEl.querySelector('.onboarding-btn-skip')?.addEventListener('click', () => this._finish());
    this.bubbleEl.querySelector('.onboarding-btn-next')?.addEventListener('click', () => this._advance());

    this._positionBubble();
    // Re-trigger the entrance animation on every step, not just the first.
    this.bubbleEl.classList.remove('active');
    void this.bubbleEl.offsetWidth;
    this.bubbleEl.classList.add('active');

    clearInterval(this._pollTimer);
    if (step.autoAdvance) {
      this._pollTimer = setInterval(() => {
        if (step.autoAdvance()) this._advance();
      }, POLL_MS);
    }
  }

  _advance() {
    clearInterval(this._pollTimer);
    this._showStep(this.stepIndex + 1);
  }

  _positionBubble() {
    if (!this.bubbleEl || typeof document === 'undefined') return;
    const avatar = document.querySelector('.boss-avatar.western');
    if (!avatar) return;
    const rect = avatar.getBoundingClientRect();
    this.bubbleEl.style.top = `${Math.round(rect.bottom + 10)}px`;
    this.bubbleEl.style.left = `${Math.round(rect.left)}px`;
  }

  _clearHighlight() {
    if (this._highlightEl) {
      this._highlightEl.classList.remove('onboarding-spotlight');
      this._highlightEl = null;
    }
  }

  _finish() {
    // Batch 4 (docs/SOUND_BATCH_PLAN.md section 4.4).
    this.game?.audio?.playOnboardingFinish?.();
    clearInterval(this._pollTimer);
    this._pollTimer = null;
    this._clearHighlight();
    window.removeEventListener('resize', this._boundReposition);

    if (this.bubbleEl) this.bubbleEl.classList.remove('active');
    const layer = this.layerEl;
    setTimeout(() => {
      if (layer?.parentNode) layer.remove();
    }, 260);
    this.layerEl = null;
    this.bubbleEl = null;
    this.active = false;

    try {
      localStorage.setItem(STORAGE_KEY, 'true');
    } catch {
      // localStorage unavailable (private browsing) - it'll just show
      // again next visit, which is a harmless fallback for a one-time tip.
    }
  }
}
